import type { CockpitRunSummary } from "../api/cockpitRunsClient";

export type ImpactKpiKey = "security" | "reliability" | "aiCost" | "observability";

export type ImpactKpi = {
  key: ImpactKpiKey;
  label: string;
  unit: "%" | "usd" | "ratio";
  value: number | null;
  trendDelta: number | null;
  threshold: { warning: number; critical: number; direction: "min" | "max" };
  source: string;
  sourceGeneratedAt: string;
  refreshFrequency: string;
  calculationRule: string;
  lineageVersion: string;
};

export type ImpactDrillDown = {
  id: string;
  lot: string;
  chantier: string;
  status: "on_track" | "watch" | "alert";
  trend: "up" | "flat" | "down";
  owner: string;
  kpiKey: ImpactKpiKey;
  source: string;
  sourceGeneratedAt: string;
  valueLabel: string;
};

export type ImpactValidationIssue = {
  type: "missing" | "outlier";
  kpiKey: ImpactKpiKey;
  message: string;
};

export const IMPACT_KPI_REFERENCE: ImpactKpi[] = [
  {
    key: "security",
    label: "Sécurité routes critiques",
    unit: "%",
    value: 100,
    trendDelta: 0,
    threshold: { warning: 98, critical: 95, direction: "min" },
    source: "artifacts/ci/security-critical-routes-report.json",
    sourceGeneratedAt: "2026-02-06T09:06:12.390Z",
    refreshFrequency: "Par lot + hebdomadaire",
    calculationRule: "controlMappedRoutes / criticalRoutes * 100",
    lineageVersion: "impact-pr8.v1",
  },
  {
    key: "reliability",
    label: "Stabilité CI (green ratio)",
    unit: "%",
    value: 40,
    trendDelta: 20,
    threshold: { warning: 95, critical: 90, direction: "min" },
    source: "artifacts/quality/nightly-summary.json",
    sourceGeneratedAt: "2026-01-07T05:20:22.480Z",
    refreshFrequency: "Hebdomadaire",
    calculationRule: "passRuns / (passRuns + failRuns) * 100",
    lineageVersion: "impact-pr8.v1",
  },
  {
    key: "aiCost",
    label: "Coût unitaire IA gouverné",
    unit: "usd",
    value: 0.000008,
    trendDelta: -95.9,
    threshold: { warning: 0.0002, critical: 0.001, direction: "max" },
    source: "artifacts/impact/pr6/cost-quality-report.json",
    sourceGeneratedAt: "2026-02-08T04:20:40.939Z",
    refreshFrequency: "Par run cost-quality",
    calculationRule: "governed.avgUnitCostUsd",
    lineageVersion: "impact-pr8.v1",
  },
  {
    key: "observability",
    label: "Disponibilité observabilité",
    unit: "%",
    value: 100,
    trendDelta: 0,
    threshold: { warning: 99, critical: 95, direction: "min" },
    source: "artifacts/observability/pr5-smoke/*/smoke-report.json",
    sourceGeneratedAt: "2026-02-08T03:08:20.245Z",
    refreshFrequency: "Par smoke + rollback",
    calculationRule: "checks(logs_exists, metrics_exists, trace_exists) pass rate",
    lineageVersion: "impact-pr8.v1",
  },
];

export const IMPACT_DRILLDOWN_REFERENCE: ImpactDrillDown[] = [
  {
    id: "pr4-lot1",
    lot: "PR#4",
    chantier: "Front critique lot 1",
    status: "on_track",
    trend: "up",
    owner: "Delivery + QA",
    kpiKey: "reliability",
    source: "artifacts/ci/e2e-critical-lot1-stability-report.json",
    sourceGeneratedAt: "2026-02-08T04:00:00.000Z",
    valueLabel: "10/10 runs PASS (100%)",
  },
  {
    id: "pr5-observability",
    lot: "PR#5",
    chantier: "Observabilité corrélée",
    status: "on_track",
    trend: "flat",
    owner: "Platform Ops",
    kpiKey: "observability",
    source: "artifacts/observability/pr5-smoke/run_20260208030815492_deadbeef/smoke-report.json",
    sourceGeneratedAt: "2026-02-08T03:08:20.245Z",
    valueLabel: "15/15 checks PASS",
  },
  {
    id: "pr6-cost",
    lot: "PR#6",
    chantier: "Gouvernance coûts IA",
    status: "on_track",
    trend: "down",
    owner: "AI Platform",
    kpiKey: "aiCost",
    source: "artifacts/impact/pr6/cost-quality-report.json",
    sourceGeneratedAt: "2026-02-08T04:20:40.939Z",
    valueLabel: "-95.9% cost vs baseline",
  },
  {
    id: "pr8-ci-watch",
    lot: "PR#8",
    chantier: "Cockpit KPI consolidé",
    status: "watch",
    trend: "up",
    owner: "Programme Impact",
    kpiKey: "reliability",
    source: "artifacts/quality/nightly-history.json",
    sourceGeneratedAt: "2025-12-28T18:01:42.182Z",
    valueLabel: "Flakiness 0.6 (target <= 0.05)",
  },
];

export function buildImpactKpisFromRuns(runs: CockpitRunSummary[]): ImpactKpi[] {
  const latestRun = runs[0];
  if (!latestRun?.metrics) return IMPACT_KPI_REFERENCE;

  return IMPACT_KPI_REFERENCE.map((kpi) => {
    if (kpi.key !== "reliability") return kpi;
    const metrics = latestRun.metrics;
    const successRate = metrics?.e2eSuccessRate;
    if (typeof successRate !== "number") return kpi;
    return {
      ...kpi,
      value: Number((successRate * 100).toFixed(1)),
      source: latestRun.artifacts?.metrics ?? kpi.source,
      sourceGeneratedAt: metrics?.generatedAt ?? latestRun.finishedAt ?? kpi.sourceGeneratedAt,
      calculationRule: "latest run metrics.e2eSuccessRate * 100",
    };
  });
}

export function validateImpactKpis(kpis: ImpactKpi[]): ImpactValidationIssue[] {
  const issues: ImpactValidationIssue[] = [];

  for (const kpi of kpis) {
    if (kpi.value === null || Number.isNaN(kpi.value)) {
      issues.push({ type: "missing", kpiKey: kpi.key, message: `${kpi.label}: donnée indisponible.` });
      continue;
    }

    const { warning, critical, direction } = kpi.threshold;
    if (direction === "min" && kpi.value < critical) {
      issues.push({ type: "outlier", kpiKey: kpi.key, message: `${kpi.label}: valeur ${kpi.value} sous seuil critique ${critical}.` });
      continue;
    }
    if (direction === "max" && kpi.value > critical) {
      issues.push({ type: "outlier", kpiKey: kpi.key, message: `${kpi.label}: valeur ${kpi.value} au-dessus seuil critique ${critical}.` });
      continue;
    }
    if (direction === "min" && kpi.value < warning) {
      issues.push({ type: "outlier", kpiKey: kpi.key, message: `${kpi.label}: vigilance, valeur ${kpi.value} sous seuil ${warning}.` });
    }
    if (direction === "max" && kpi.value > warning) {
      issues.push({ type: "outlier", kpiKey: kpi.key, message: `${kpi.label}: vigilance, valeur ${kpi.value} au-dessus seuil ${warning}.` });
    }
  }

  return issues;
}
